/**
 * Health Supplements Collection JavaScript - FIXED
 * Handles filtering, sorting, and product interactions
 */

class HealthSupplementsCollection {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.filterForm = section.querySelector('[data-filter-form]');
    this.filterSidebar = section.querySelector('[data-filter-sidebar]');
    this.filterToggle = section.querySelector('[data-filter-toggle]');
    this.filterClose = section.querySelector('[data-filter-close]'); // FIXED: Added selector
    this.filterOverlay = section.querySelector('[data-filter-overlay]'); // FIXED: Added selector
    this.productsGrid = section.querySelector('[data-products-grid]');
    this.loadingOverlay = section.querySelector('[data-loading-overlay]');
    this.sortSelect = section.querySelector('[data-sort-select]');
    
    this.filters = {};
    this.currentPage = 1;
    this.isLoading = false;
    this.abortController = null;
    this.filterDebounceTimer = null;
    
    this.init();
  }
  
  init() {
    // Debug log to check elements are found
    if (window.location.href.includes('#debug')) {
      console.log('HealthSupplementsCollection initialized', {
        section: this.section,
        filterToggle: this.filterToggle,
        filterSidebar: this.filterSidebar,
        filterClose: this.filterClose,
        filterOverlay: this.filterOverlay,
        filterForm: this.filterForm
      });
    }
    
    this.bindEvents();
    this.initializeFilters();
    this.initializeProductCards();
    this.initializeViewOptions();
    this.updateURLFromFilters();
    
    // FIXED: Set initial filter badge count
    this.updateFilterBadge();
    
    // FIXED: Setup auto-apply on initial load
    this.setupAutoApply();
  }
  
  bindEvents() {
    // Filter form submission
    if (this.filterForm) {
      this.filterForm.addEventListener('submit', this.handleFilterSubmit.bind(this));
    }
    
    // FIXED: Mobile filter toggle - Use separate binding method
    this.bindFilterToggle();
    
    // Filter group toggles
    const filterGroupToggles = this.section.querySelectorAll('.filter-group__toggle');
    filterGroupToggles.forEach(toggle => {
      toggle.addEventListener('click', this.toggleFilterGroup.bind(this));
    });
    
    // Clear all filters - FIXED: Use separate binding method
    this.bindClearAllButton();
    
    // Sort dropdown
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', this.handleSortChange.bind(this));
    }
    
    // Pagination
    this.bindPaginationEvents();
    
    // Browser back/forward
    window.addEventListener('popstate', this.handlePopState.bind(this));
    
    // ESC key to close filters on mobile
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.filterSidebar?.classList.contains('active')) {
        this.closeMobileFilters();
      }
    });
  }
  
  // FIXED: Setup auto-apply for desktop
  setupAutoApply() {
    const isDesktop = window.innerWidth >= 990;
    
    if (!isDesktop || !this.filterForm) return;
    
    // Remove all existing listeners first by cloning elements
    const checkboxes = this.filterForm.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      const newCheckbox = checkbox.cloneNode(true);
      checkbox.replaceWith(newCheckbox);
      
      // Add fresh listener
      newCheckbox.addEventListener('change', () => {
        this.applyFilters();
      });
    });
  }
  
  // FIXED: Separate method to bind clear all button
  bindClearAllButton() {
    const clearAllBtn = this.section.querySelector('[data-clear-filters]');
    if (clearAllBtn) {
      // Remove old listener if exists
      clearAllBtn.replaceWith(clearAllBtn.cloneNode(true));
      const newClearBtn = this.section.querySelector('[data-clear-filters]');
      if (newClearBtn) {
        newClearBtn.addEventListener('click', this.clearAllFilters.bind(this));
      }
    }
  }
  
  // FIXED: Separate method to bind filter toggle
  bindFilterToggle() {
    this.filterToggle = this.section.querySelector('[data-filter-toggle]');
    this.filterClose = this.section.querySelector('[data-filter-close]');
    this.filterOverlay = this.section.querySelector('[data-filter-overlay]');
    
    if (this.filterToggle) {
      // Remove old listener by cloning
      const newToggle = this.filterToggle.cloneNode(true);
      this.filterToggle.replaceWith(newToggle);
      this.filterToggle = newToggle;
      
      this.filterToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openMobileFilters();
      });
    }
    
    if (this.filterClose) {
      const newClose = this.filterClose.cloneNode(true);
      this.filterClose.replaceWith(newClose);
      this.filterClose = newClose;
      
      this.filterClose.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeMobileFilters();
      });
    }
    
    if (this.filterOverlay) {
      const newOverlay = this.filterOverlay.cloneNode(true);
      this.filterOverlay.replaceWith(newOverlay);
      this.filterOverlay = newOverlay;
      
      this.filterOverlay.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeMobileFilters();
      });
    }
  }
  
  initializeFilters() {
    // Get initial filter state from URL
    const params = new URLSearchParams(window.location.search);
    
    // Set checkbox states from URL
    const filterInputs = this.filterForm?.querySelectorAll('input[type="checkbox"]');
    filterInputs?.forEach(input => {
      const paramValue = params.get(input.name);
      if (paramValue === input.value) {
        input.checked = true;
      }
    });
  }
  
  initializeProductCards() {
    const productForms = this.section.querySelectorAll('[data-product-form]');
    
    productForms.forEach(form => {
      // Remove any existing listeners to prevent duplicates
      const newForm = form.cloneNode(true);
      form.parentNode.replaceChild(newForm, form);
      
      // Add fresh listener
      newForm.addEventListener('submit', this.handleQuickAdd.bind(this));
    });
  }
  
  initializeViewOptions() {
    const viewOptions = this.section.querySelectorAll('[data-view]');
    
    viewOptions.forEach(option => {
      option.addEventListener('click', (e) => {
        e.preventDefault();
        const view = option.dataset.view;
        
        // Update active state
        viewOptions.forEach(opt => opt.classList.remove('active'));
        option.classList.add('active');
        
        // Update grid view
        if (this.productsGrid) {
          this.productsGrid.dataset.view = view;
          
          // Save preference
          localStorage.setItem('collection-view', view);
        }
      });
    });
    
    // Restore saved view preference
    const savedView = localStorage.getItem('collection-view');
    if (savedView && this.productsGrid) {
      this.productsGrid.dataset.view = savedView;
      viewOptions.forEach(opt => {
        opt.classList.toggle('active', opt.dataset.view === savedView);
      });
    }
  }
  
  handleFilterSubmit(e) {
    e.preventDefault();
    this.applyFilters();
  }
  
  handleSortChange(e) {
    this.applyFilters();
  }
  
  async applyFilters() {
    if (this.isLoading) {
      // Cancel previous request
      if (this.abortController) {
        this.abortController.abort();
      }
    }
    
    this.isLoading = true;
    this.showLoading();
    
    // Create new abort controller
    this.abortController = new AbortController();
    
    try {
      // Build URL with filters
      const formData = new FormData(this.filterForm);
      const params = new URLSearchParams();
      
      // Add form data to params
      for (const [key, value] of formData.entries()) {
        if (value && value !== '') {
          params.append(key, value);
        }
      }
      
      // Add sort parameter
      if (this.sortSelect) {
        params.set('sort_by', this.sortSelect.value);
      }
      
      // Add page parameter if needed
      if (this.currentPage > 1) {
        params.set('page', this.currentPage);
      }
      
      const url = `${window.location.pathname}?${params.toString()}`;
      
      // Fetch filtered results
      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const html = await response.text();
      
      // Parse response
      const parser = new DOMParser();
      const newDoc = parser.parseFromString(html, 'text/html');
      
      // Update products grid
      const newProductsGrid = newDoc.querySelector('[data-products-grid]');
      if (newProductsGrid && this.productsGrid) {
        this.productsGrid.innerHTML = newProductsGrid.innerHTML;
      }
      
      // Update pagination
      const newPagination = newDoc.querySelector('.pagination');
      const currentPagination = this.section.querySelector('.pagination');
      if (newPagination && currentPagination) {
        currentPagination.innerHTML = newPagination.innerHTML;
        this.bindPaginationEvents();
      } else if (currentPagination && !newPagination) {
        // Remove pagination if not needed
        currentPagination.remove();
      }
      
      // Update active filters display
      const newActiveFilters = newDoc.querySelector('.active-filters');
      const currentActiveFilters = this.section.querySelector('.active-filters');
      if (currentActiveFilters) {
        if (newActiveFilters) {
          currentActiveFilters.innerHTML = newActiveFilters.innerHTML;
          currentActiveFilters.style.display = 'block';
        } else {
          currentActiveFilters.style.display = 'none';
        }
      }
      
      // Update filter counts
      this.updateFilterCounts(newDoc);
      
      // FIXED: Update mobile filter badge
      this.updateFilterBadge();
      
      // FIXED: Rebind filter controls after AJAX
      this.bindFilterToggle();
      this.bindClearAllButton();
      
      // Rebind filter group toggles
      const filterGroupToggles = this.section.querySelectorAll('.filter-group__toggle');
      filterGroupToggles.forEach(toggle => {
        toggle.addEventListener('click', this.toggleFilterGroup.bind(this));
      });
      
      // FIXED: Re-setup auto-apply for desktop after AJAX
      this.setupAutoApply();
      
      // Update URL
      window.history.pushState({}, '', url);
      
      // Reinitialize product cards
      this.initializeProductCards();
      
      // Close mobile filters if open
      this.closeMobileFilters();
      
      // Scroll to top
      this.scrollToProducts();
      
      // Trigger custom event
      this.triggerFilteredEvent();
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Filter error:', error);
        this.showError('Failed to apply filters. Please try again.');
      }
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }
  
  updateFilterCounts(newDoc) {
    // Update filter counts based on new results
    const newFilterGroups = newDoc.querySelectorAll('.filter-group');
    
    newFilterGroups.forEach(newGroup => {
      const groupId = newGroup.dataset.filterGroup;
      // Skip price filter group if it somehow exists
      if (groupId === 'price') return;
      
      const currentGroup = this.section.querySelector(`[data-filter-group="${groupId}"]`);
      
      if (currentGroup) {
        // Update counts for each filter option
        const newCounts = newGroup.querySelectorAll('.filter-count');
        const currentCounts = currentGroup.querySelectorAll('.filter-count');
        
        newCounts.forEach((newCount, index) => {
          if (currentCounts[index]) {
            currentCounts[index].textContent = newCount.textContent;
          }
        });
        
        // Disable options with zero count
        const newInputs = newGroup.querySelectorAll('input[type="checkbox"]');
        const currentInputs = currentGroup.querySelectorAll('input[type="checkbox"]');
        
        newInputs.forEach((newInput, index) => {
          if (currentInputs[index]) {
            currentInputs[index].disabled = newInput.disabled;
          }
        });
      }
    });
  }
  
  // FIXED: Update mobile filter badge count
  updateFilterBadge() {
    const filterBadge = this.section.querySelector('.filter-badge');
    if (!filterBadge) return;
    
    // Count active filters
    let activeCount = 0;
    
    // Count checked checkboxes
    const checkboxes = this.filterForm?.querySelectorAll('input[type="checkbox"]:checked');
    activeCount += checkboxes?.length || 0;
    
    // Update badge display
    if (activeCount > 0) {
      filterBadge.textContent = activeCount;
      filterBadge.style.display = 'inline-flex';
    } else {
      filterBadge.style.display = 'none';
    }
  }
  
  clearAllFilters(e) {
    e?.preventDefault();
    
    // Clear all checkboxes
    const checkboxes = this.filterForm?.querySelectorAll('input[type="checkbox"]');
    checkboxes?.forEach(checkbox => {
      checkbox.checked = false;
    });
    
    // FIXED: Update badge immediately after clearing
    this.updateFilterBadge();
    
    // Apply empty filters
    this.applyFilters();
  }
  
  toggleFilterGroup(e) {
    const toggle = e.currentTarget;
    const group = toggle.closest('.filter-group');
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    
    toggle.setAttribute('aria-expanded', !expanded);
    group.classList.toggle('collapsed', expanded);
  }
  
  // FIXED: Open mobile filters
  openMobileFilters() {
    if (this.filterSidebar) {
      // Add active class to sidebar
      this.filterSidebar.classList.add('active');
      // Add class to body
      document.body.classList.add('filter-open');
      
      // Focus management
      this.trapFocus(this.filterSidebar);
      
      // Debug
      if (window.location.href.includes('#debug')) {
        console.log('Opening mobile filters', {
          filterSidebar: this.filterSidebar,
          hasActiveClass: this.filterSidebar.classList.contains('active'),
          bodyClass: document.body.classList.contains('filter-open')
        });
      }
    }
  }
  
  // FIXED: Close mobile filters
  closeMobileFilters() {
    if (this.filterSidebar) {
      // Remove active class from sidebar
      this.filterSidebar.classList.remove('active');
      // Remove class from body
      document.body.classList.remove('filter-open');
      
      // Release focus trap
      this.releaseFocus();
      
      // Debug
      if (window.location.href.includes('#debug')) {
        console.log('Closing mobile filters');
      }
    }
  }
  
  trapFocus(element) {
    const focusableElements = element.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
    );
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];
    
    const trapFocusHandler = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            lastFocusableElement.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusableElement) {
            firstFocusableElement.focus();
            e.preventDefault();
          }
        }
      }
      
      if (e.key === 'Escape') {
        this.closeMobileFilters();
      }
    };
    
    element.addEventListener('keydown', trapFocusHandler);
    element.trapFocusHandler = trapFocusHandler;
    
    // Focus first element
    firstFocusableElement?.focus();
  }
  
  releaseFocus() {
    if (this.filterSidebar?.trapFocusHandler) {
      this.filterSidebar.removeEventListener('keydown', this.filterSidebar.trapFocusHandler);
      delete this.filterSidebar.trapFocusHandler;
    }
    
    // Return focus to trigger
    this.filterToggle?.focus();
  }
  
  async handleQuickAdd(e) {
    e.preventDefault();
    
    const form = e.target;
    const button = form.querySelector('button[type="submit"]');
    const originalText = button.textContent.trim();
    
    // Prevent multiple clicks
    if (button.disabled || button.classList.contains('loading')) return;
    
    // Update button state
    button.textContent = 'Adding...';
    button.disabled = true;
    button.classList.add('loading');
    button.classList.remove('success', 'error');
    
    try {
      const formData = new FormData(form);
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: formData.get('id'),
          quantity: 1,
          sections: 'cart-drawer,cart-icon-bubble'
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      // Success feedback
      button.textContent = 'Added!';
      button.classList.remove('loading');
      button.classList.add('success');
      button.disabled = true; // Keep disabled during success state
      
      // Update cart UI
      this.updateCart(data);
      
      // Show notification
      this.showNotification('Product added to cart');
      
      // Reset button after delay
      setTimeout(() => {
        button.textContent = originalText;
        button.classList.remove('success');
        button.disabled = false;
      }, 2000);
      
    } catch (error) {
      console.error('Add to cart error:', error);
      
      // Error feedback
      button.textContent = 'Error';
      button.classList.remove('loading');
      button.classList.add('error');
      
      this.showNotification('Failed to add product', 'error');
      
      setTimeout(() => {
        button.textContent = originalText;
        button.classList.remove('error');
        button.disabled = false;
      }, 2000);
    }
  }
  
  updateCart(data) {
    // Dispatch custom event for cart updates
    document.dispatchEvent(new CustomEvent('cart:updated', { 
      detail: data,
      bubbles: true
    }));
    
    // Update cart bubble count if exists
    const cartBubble = document.querySelector('.cart-count-bubble');
    if (cartBubble && data.item_count !== undefined) {
      cartBubble.textContent = data.item_count;
      cartBubble.style.display = data.item_count > 0 ? 'flex' : 'none';
    }
    
    // Update cart drawer if exists
    if (data.sections) {
      for (const [sectionId, html] of Object.entries(data.sections)) {
        const section = document.getElementById(sectionId);
        if (section) {
          section.innerHTML = html;
        }
      }
    }
  }
  
  bindPaginationEvents() {
    const paginationLinks = this.section.querySelectorAll('.pagination a');
    
    paginationLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        
        const url = new URL(link.href);
        const page = url.searchParams.get('page') || 1;
        
        this.currentPage = parseInt(page);
        this.applyFilters();
      });
    });
  }
  
  handlePopState(e) {
    // Handle browser back/forward
    this.initializeFilters();
    this.applyFilters();
  }
  
  updateURLFromFilters() {
    // Update URL when filters change without page reload
    const formData = new FormData(this.filterForm);
    const params = new URLSearchParams();
    
    for (const [key, value] of formData.entries()) {
      if (value) {
        params.append(key, value);
      }
    }
    
    if (this.sortSelect) {
      params.set('sort_by', this.sortSelect.value);
    }
    
    const url = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    
    if (url !== window.location.href) {
      window.history.replaceState({}, '', url);
    }
  }
  
  scrollToProducts() {
    const productsTop = this.productsGrid?.getBoundingClientRect().top + window.scrollY - 100;
    
    if (productsTop) {
      window.scrollTo({
        top: productsTop,
        behavior: 'smooth'
      });
    }
  }
  
  showLoading() {
    if (this.loadingOverlay) {
      this.loadingOverlay.style.display = 'flex';
      this.loadingOverlay.setAttribute('aria-hidden', 'false');
    }
    
    // Add loading state to grid
    this.productsGrid?.classList.add('loading');
  }
  
  hideLoading() {
    if (this.loadingOverlay) {
      this.loadingOverlay.style.display = 'none';
      this.loadingOverlay.setAttribute('aria-hidden', 'true');
    }
    
    // Remove loading state from grid
    this.productsGrid?.classList.remove('loading');
  }
  
  showNotification(message, type = 'success') {
    // Create notification element
    const notification = document.createElement('div');
    notification.className = `notification notification--${type}`;
    notification.textContent = message;
    notification.setAttribute('role', 'alert');
    
    // Add to page
    document.body.appendChild(notification);
    
    // Trigger animation
    setTimeout(() => {
      notification.classList.add('show');
    }, 100);
    
    // Remove after delay
    setTimeout(() => {
      notification.classList.remove('show');
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 3000);
  }
  
  showError(message) {
    this.showNotification(message, 'error');
  }
  
  triggerFilteredEvent() {
    // Trigger custom event when filters are applied
    const event = new CustomEvent('collection:filtered', {
      detail: {
        sectionId: this.sectionId,
        filters: this.getActiveFilters()
      },
      bubbles: true
    });
    
    this.section.dispatchEvent(event);
  }
  
  getActiveFilters() {
    const filters = {};
    
    // Get checkbox filters
    const checkboxes = this.filterForm?.querySelectorAll('input[type="checkbox"]:checked');
    checkboxes?.forEach(checkbox => {
      if (!filters[checkbox.name]) {
        filters[checkbox.name] = [];
      }
      filters[checkbox.name].push(checkbox.value);
    });
    
    // Get sort
    if (this.sortSelect?.value) {
      filters.sort_by = this.sortSelect.value;
    }
    
    return filters;
  }
  
  destroy() {
    // Clean up event listeners
    this.filterForm?.removeEventListener('submit', this.handleFilterSubmit);
    window.removeEventListener('popstate', this.handlePopState);
    
    // Clear any pending timers
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
    }
    
    // Cancel any pending requests
    if (this.abortController) {
      this.abortController.abort();
    }
  }
}

// Notification styles
const notificationStyles = `
  .notification {
    position: fixed;
    top: 20px;
    right: 20px;
    padding: 1rem 1.5rem;
    background: var(--primary-color);
    color: white;
    border-radius: 4px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transform: translateX(400px);
    transition: transform 0.3s ease;
    z-index: 10000;
    max-width: 300px;
    font-size: 0.9375rem;
    font-weight: 500;
  }
  
  .notification.show {
    transform: translateX(0);
  }
  
  .notification--error {
    background: #E74C3C;
  }
  
  .notification--warning {
    background: #F39C12;
  }
  
  .btn--primary.loading {
    background: #ccc;
    cursor: wait;
  }
  
  .btn--primary.success {
    background: #27AE60;
  }
  
  .btn--primary.error {
    background: #E74C3C;
  }
  
  @media (max-width: 749px) {
    .notification {
      right: 10px;
      left: 10px;
      max-width: none;
    }
  }
`;

// Add notification styles to page
if (!document.getElementById('notification-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'notification-styles';
  styleSheet.textContent = notificationStyles;
  document.head.appendChild(styleSheet);
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  // Initialize all collection sections on page
  const collectionSections = document.querySelectorAll('[data-section-type="collection-health"]');
  
  if (collectionSections.length === 0) {
    // Try alternate selector
    const altSections = document.querySelectorAll('.collection-section');
    altSections.forEach(section => {
      if (section.querySelector('[data-filter-form]')) {
        // Store instance on element for later access
        section.collectionInstance = new HealthSupplementsCollection(section);
      }
    });
  } else {
    collectionSections.forEach(section => {
      // Store instance on element for later access
      section.collectionInstance = new HealthSupplementsCollection(section);
    });
  }
  
  // Debug helper
  if (window.location.href.includes('#debug')) {
    console.log('Collection sections initialized:', collectionSections.length);
  }
});

// Theme Editor support
document.addEventListener('shopify:section:load', (e) => {
  const section = document.querySelector(`[data-section-id="${e.detail.sectionId}"]`);
  
  if (section && section.dataset.sectionType === 'collection-health') {
    // Clean up old instance if exists
    if (section.collectionInstance) {
      section.collectionInstance.destroy();
    }
    // Create new instance
    section.collectionInstance = new HealthSupplementsCollection(section);
  }
});

document.addEventListener('shopify:section:unload', (e) => {
  const section = document.querySelector(`[data-section-id="${e.detail.sectionId}"]`);
  
  if (section && section.dataset.sectionType === 'collection-health') {
    // Clean up instance
    if (section.collectionInstance) {
      section.collectionInstance.destroy();
      delete section.collectionInstance;
    }
  }
});

// Export for use in other scripts
window.HealthSupplementsCollection = HealthSupplementsCollection;